import { Dispatch, ReactNode, SetStateAction } from 'react';
import { useTranslation } from '@frontend/i18n';
import { ScopedLocationFilterMenu } from '@frontend/location-filter-menu';
import { theme } from '@frontend/theme';
import { Heading, NakedButton, Text } from '@frontend/design-system';
import { ACCORDION_WIDTH } from './constants';

type Props = {
  filteredLocationIds?: string[];
  locationSelectorSlot?: ReactNode;
  manageAllButtonProps?: {
    disabled?: boolean;
    trackingId?: string;
  };
  openModal?: () => void;
  setFilteredLocationIds?: Dispatch<SetStateAction<string[]>>;
  title?: string;
};

/**
 * This component renders the header for the Weave Assistant Multi Control.
 * @param filteredLocationIds - array of location IDs the user has filtered by; only used by the default location filter menu
 * @param locationSelectorSlot - optional node to render in place of the default location filter menu
 * @param manageAllButtonProps - optional props for the manage all button (disabled, trackingId)
 * @param openModal - optional function to open the modal for managing all locations
 * @param setFilteredLocationIds - function to set the filtered location IDs state; only used by the default location filter menu
 * @param title - optional title for the control
 * @returns MultiLocationAccordionControlHeader component
 */
export const MultiLocationAccordionControlHeader = ({
  filteredLocationIds,
  locationSelectorSlot,
  manageAllButtonProps,
  openModal,
  setFilteredLocationIds,
  title,
}: Props) => {
  const { t } = useTranslation('settings');

  return (
    <div
      css={{
        display: 'flex',
        width: ACCORDION_WIDTH,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {title && <Heading level={2}>{title}</Heading>}
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(2),
          alignItems: 'center',
          width: title ? 'auto' : '100%',
          justifyContent: title ? 'flex-end' : 'space-between',
        }}
      >
        {locationSelectorSlot ?? (
          <ScopedLocationFilterMenu
            onChange={(locationIds) => {
              const newSelectedLocations = [...new Set(locationIds)];
              setFilteredLocationIds?.(newSelectedLocations);
            }}
            selectedOptions={filteredLocationIds ?? []}
            showClearAll={false}
          />
        )}
        {openModal && (
          <NakedButton
            disabled={manageAllButtonProps?.disabled}
            onClick={openModal}
            trackingId={manageAllButtonProps?.trackingId}
          >
            <Text size='medium'>{t('Manage All')}</Text>
          </NakedButton>
        )}
      </div>
    </div>
  );
};
